/**
 * ModernFusion — content model + resolver.
 *
 * Urban Indian engagement: minimalist, glassmorphism, playlist features.
 */

import type { InvitationData } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
  safeFormatDateLong,
  safeFormatTime,
} from '@/src/infrastructure/lib/weddingDataHelper';

function img(id: string, w = 1400): string {
  return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;
}

const STOCK = {
  hero: img('1519225421980-715cb0215aed', 2000),
  couple: img('1529636798458-92182e662485', 900),
  venue: img('1464366400600-7168b8af9bc3', 900),
  party: img('1533174072545-e8d4aa97edf9', 900),
};

export interface Event {
  title: string;
  date: string;
  time: string;
  venue: string;
  description: string;
  dressCode?: string;
}

export interface ModernContent {
  hero: {
    kicker: string;
    title: string;
    subtitle: string;
    host: string;
    dateLabel: string;
    venueShort: string;
    background: string;
  };
  story: {
    label: string;
    title: string;
    chapters: { title: string; description: string }[];
  };
  events: {
    label: string;
    title: string;
    items: Event[];
  };
  rsvp: {
    label: string;
    title: string;
    description: string;
    deadline: string;
    dietaryNote?: string;
    externalLink?: string;
  };
  footer: {
    message: string;
    signature: string;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────

const hasText = (v?: string | null): v is string =>
  typeof v === 'string' && v.trim().length > 0;

function formatLong(iso?: string): string {
  if (!hasText(iso)) return 'Date to be announced';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function formatTime(iso?: string, fallback = ''): string {
  if (!hasText(iso)) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

// ── Demo Content ───────────────────────────────────────────────────────────

const DEMO: ModernContent = {
  hero: {
    kicker: 'Two souls, one journey',
    title: 'Engagement & Celebration',
    subtitle: 'are celebrating their engagement',
    host: 'Warm Regards, Family & Friends',
    dateLabel: 'Celebration Date',
    venueShort: 'Celebration Venue',
    background: STOCK.hero,
  },
  story: {
    label: 'Our Story',
    title: 'From togetherness to forever',
    chapters: [
      { title: 'First Meeting', description: 'When paths crossed and stories began' },
      { title: 'The Journey', description: 'Memories, laughter, and lifelong promises' },
      { title: 'The Celebration', description: 'Join us to celebrate our new beginning' },
    ],
  },
  events: {
    label: 'Celebrations',
    title: 'A celebration of joy',
    items: [
      {
        title: 'Cocktail Evening',
        date: 'Day 1',
        time: '7:00 PM',
        venue: 'Main Lounge',
        description: 'Meet and greet with drinks and music',
        dressCode: 'Smart Casual',
      },
      {
        title: 'Engagement Ceremony',
        date: 'Day 2',
        time: '6:00 PM',
        venue: 'Grand Ballroom',
        description: 'Ring exchange followed by dinner and dancing',
        dressCode: 'Indian Fusion',
      },
    ],
  },
  rsvp: {
    label: 'Join Us',
    title: 'Kindly Respond',
    description: 'We would love to celebrate with you. Please let us know by the date below.',
    deadline: 'RSVP in advance',
    dietaryNote: 'Please mention any dietary preferences',
  },
  footer: {
    message: 'With love and excitement',
    signature: 'The Couple & Families',
  },
};

// ── Resolver ──────────────────────────────────────────────────────────────

export function resolveContent(data: InvitationData): ModernContent {
  const wedding = data.customData?.wedding ?? {};
  const extracted = extractCoupleNames(data.eventName);

  const groomName = wedding.groom?.fullName || wedding.groom?.nickname || extracted?.groom || 'Groom';
  const brideName = wedding.bride?.fullName || wedding.bride?.nickname || extracted?.bride || 'Bride';
  const coupleNames = (extracted?.groom && extracted?.bride)
    ? `${extracted.groom} & ${extracted.bride}`
    : ((wedding.groom?.fullName || wedding.groom?.nickname) && (wedding.bride?.fullName || wedding.bride?.nickname))
      ? `${wedding.groom.fullName || wedding.groom.nickname} & ${wedding.bride.fullName || wedding.bride.nickname}`
      : (hasText(data.eventName) ? data.eventName.trim() : `${groomName} & ${brideName}`);

  const effectiveDate = parseFlexibleDate(data.eventDate) || new Date(Date.now() + 30 * 86400000);
  const prevDate = new Date(effectiveDate.getTime() - 86400000);
  const venueShort = hasText(data.eventVenue) ? data.eventVenue : 'Venue to be announced';

  const scheduleEvents: Event[] | undefined = wedding.weddingEvents?.length
    ? wedding.weddingEvents.map((e: any) => ({
        title: e.title,
        date: safeFormatDateLong(e.date),
        time: [safeFormatTime(e.startTime), safeFormatTime(e.endTime)].filter(Boolean).join(' – '),
        venue: e.venue || venueShort,
        description: e.description || '',
        dressCode: e.dressCode || 'Smart Casual',
      }))
    : [
        {
          title: 'Cocktail Evening',
          date: safeFormatDateLong(prevDate),
          time: '7:00 PM',
          venue: venueShort,
          description: 'Meet and greet with drinks and music',
          dressCode: 'Smart Casual',
        },
        {
          title: 'Engagement Ceremony',
          date: safeFormatDateLong(effectiveDate),
          time: '6:00 PM',
          venue: venueShort,
          description: 'Ring exchange followed by dinner and dancing',
          dressCode: 'Indian Fusion',
        },
      ];

  return {
    hero: {
      kicker: DEMO.hero.kicker,
      title: coupleNames,
      subtitle: DEMO.hero.subtitle,
      host: hasText(data.hostName) ? data.hostName : (hasText(data.eventName) ? coupleNames : DEMO.hero.host),
      dateLabel: hasText(data.eventDate) ? safeFormatDateLong(data.eventDate) : 'Date to be announced',
      venueShort,
      background: data.mediaUrls?.[0] ?? DEMO.hero.background,
    },
    story: DEMO.story,
    events: {
      ...DEMO.events,
      items: scheduleEvents,
    },
    rsvp: {
      ...DEMO.rsvp,
      description: hasText(data.customMessage) ? data.customMessage : DEMO.rsvp.description,
      externalLink: hasText(data.rsvpLink) ? data.rsvpLink : undefined,
    },
    footer: {
      message: DEMO.footer.message,
      signature: coupleNames,
    },
  };
}
